import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Edit, Plus, X } from "lucide-react";
import { toast } from "sonner";

interface EditableAtAGlanceProps {
  value: any;
  onSave: (value: any) => Promise<void>;
}

export const EditableAtAGlance = ({ value, onSave }: EditableAtAGlanceProps) => {
  const [open, setOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  const [quickSummary, setQuickSummary] = useState(value?.quick_summary || "");
  const [skillLevel, setSkillLevel] = useState(value?.who_its_for?.skill_level || "");
  const [typicalProfile, setTypicalProfile] = useState(value?.who_its_for?.typical_profile || "");
  const [otherCriteria, setOtherCriteria] = useState<string[]>(value?.who_its_for?.other_criteria || []);
  const [whatYouGet, setWhatYouGet] = useState<string[]>(value?.what_you_get || []);
  const [minimumPeriod, setMinimumPeriod] = useState(value?.commitment?.minimum_period || "");
  const [cancellationTerms, setCancellationTerms] = useState(value?.commitment?.cancellation_terms || "");
  const [guaranteeType, setGuaranteeType] = useState(value?.guarantee?.type || "");
  const [guaranteeTerms, setGuaranteeTerms] = useState(value?.guarantee?.terms || "");
  const [setupTime, setSetupTime] = useState(value?.speed_to_value?.setup_time || "");
  const [firstImpact, setFirstImpact] = useState(value?.speed_to_value?.first_impact || "");

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const newValue = {
        quick_summary: quickSummary,
        who_its_for: {
          skill_level: skillLevel,
          typical_profile: typicalProfile,
          other_criteria: otherCriteria.filter(c => c.trim()),
        },
        what_you_get: whatYouGet.filter(w => w.trim()),
        commitment: {
          minimum_period: minimumPeriod,
          cancellation_terms: cancellationTerms,
        },
        guarantee: {
          type: guaranteeType,
          terms: guaranteeTerms,
        },
        speed_to_value: {
          setup_time: setupTime,
          first_impact: firstImpact,
        },
      };

      await onSave(newValue);
      toast.success("At-a-glance info updated");
      setOpen(false);
    } catch (error) {
      console.error('Error saving at-a-glance:', error);
      toast.error("Failed to save at-a-glance info");
    } finally {
      setIsSaving(false);
    }
  };

  const addArrayItem = (setter: (items: string[]) => void, items: string[]) => {
    setter([...items, ""]);
  };

  const updateArrayItem = (setter: (items: string[]) => void, items: string[], index: number, value: string) => {
    const newItems = [...items];
    newItems[index] = value;
    setter(newItems);
  };

  const removeArrayItem = (setter: (items: string[]) => void, items: string[], index: number) => {
    setter(items.filter((_, i) => i !== index));
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          <Edit className="w-4 h-4 mr-2" />
          {value ? "Edit At-A-Glance" : "Add At-A-Glance Info"}
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit At-A-Glance Information</DialogTitle>
          <DialogDescription>
            Configure the structured at-a-glance section for this product
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Quick Summary */}
          <div className="space-y-2">
            <Label htmlFor="quick-summary">Quick Summary</Label>
            <Textarea
              id="quick-summary"
              placeholder="One sentence value proposition..."
              value={quickSummary}
              onChange={(e) => setQuickSummary(e.target.value)}
              rows={2}
            />
          </div>

          {/* Who It's For */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Who It's For</Label>
            <div className="space-y-2">
              <Label htmlFor="skill-level">Skill Level</Label>
              <Input
                id="skill-level"
                placeholder="e.g., Intermediate to Advanced"
                value={skillLevel}
                onChange={(e) => setSkillLevel(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="typical-profile">Typical Profile</Label>
              <Input
                id="typical-profile"
                placeholder="e.g., 12 to 100+ closings per year"
                value={typicalProfile}
                onChange={(e) => setTypicalProfile(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>Other Criteria (optional)</Label>
              {otherCriteria.map((criteria, idx) => (
                <div key={idx} className="flex gap-2">
                  <Input
                    placeholder="Additional criterion..."
                    value={criteria}
                    onChange={(e) => updateArrayItem(setOtherCriteria, otherCriteria, idx, e.target.value)}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeArrayItem(setOtherCriteria, otherCriteria, idx)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => addArrayItem(setOtherCriteria, otherCriteria)}
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Criteria
              </Button>
            </div>
          </div>

          {/* What You Get */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">What You Get</Label>
            {whatYouGet.map((item, idx) => (
              <div key={idx} className="flex gap-2">
                <Input
                  placeholder="Deliverable or outcome..."
                  value={item}
                  onChange={(e) => updateArrayItem(setWhatYouGet, whatYouGet, idx, e.target.value)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => removeArrayItem(setWhatYouGet, whatYouGet, idx)}
                >
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ))}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => addArrayItem(setWhatYouGet, whatYouGet)}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Item
            </Button>
          </div>

          {/* Commitment */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Commitment</Label>
            <div className="space-y-2">
              <Label htmlFor="minimum-period">Minimum Period</Label>
              <Input
                id="minimum-period"
                placeholder="e.g., 6-month minimum"
                value={minimumPeriod}
                onChange={(e) => setMinimumPeriod(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cancellation-terms">Cancellation Terms</Label>
              <Input
                id="cancellation-terms"
                placeholder="e.g., Cancel anytime after first 30 days"
                value={cancellationTerms}
                onChange={(e) => setCancellationTerms(e.target.value)}
              />
            </div>
          </div>

          {/* Guarantee */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Guarantee</Label>
            <div className="space-y-2">
              <Label htmlFor="guarantee-type">Guarantee Type</Label>
              <Input
                id="guarantee-type"
                placeholder="e.g., Satisfaction guarantee"
                value={guaranteeType}
                onChange={(e) => setGuaranteeType(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="guarantee-terms">Terms</Label>
              <Textarea
                id="guarantee-terms"
                placeholder="e.g., Credit toward other programs if not satisfied"
                value={guaranteeTerms}
                onChange={(e) => setGuaranteeTerms(e.target.value)}
                rows={2}
              />
            </div>
          </div>

          {/* Speed to Value */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Speed to Value</Label>
            <div className="space-y-2">
              <Label htmlFor="setup-time">Setup Time</Label>
              <Input
                id="setup-time"
                placeholder="e.g., 7 days to onboard"
                value={setupTime}
                onChange={(e) => setSetupTime(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="first-impact">First Impact</Label>
              <Input
                id="first-impact"
                placeholder="e.g., typically seen in 30-90 days"
                value={firstImpact}
                onChange={(e) => setFirstImpact(e.target.value)}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save Changes"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
